// Imported from the module, not the `../library` barrel: the barrel pulls the whole component
// library (activity renderers and their axios-backed request modules) into anything that touches it,
// which a pure helper — and its unit test — has no reason to load.
import { isWithinTimeFilterWindow } from "../library/utilization/DateUtilization";

// Ön koşul çalışmaları — what a learner has ACTUALLY SOLVED on the readiness (hazır bulunuşluk) path.
//
// The readiness walk in the learner app hands a weak outcome's prerequisites to the learner one konu
// at a time; practising one creates a personalized assignment carrying `triggerType: "READINESS"` and
// `triggerId: <the prerequisite categoryId>`. Those rows are the only per-learner record that the work
// happened — readiness itself is recomputed from evaluation history on every request and stores nothing.
//
// This model deliberately reports SOLVED WORK ONLY, never the prerequisite graph: the report answers
// "what have I actually done", and the full prerequisite list of a level is mostly konu the learner
// has never touched — listing those turns a record of effort into a backlog. `isEvaluated` is the
// exact gate — the backend only ever sets it once ActivityEvaluation rows exist, and those are
// written when the learner presses Complete. Work that was started and abandoned never appears here.
//
// Kept in sync with catchup-admin-web's copy of the same name, which feeds the staff-facing learner
// modal: both consoles must agree on what counts as solved prerequisite work.

/** One completed readiness assignment, as it reads in the drill-down. */
export interface PrerequisiteWorkAssignmentRow {
  personalizedAssignmentId: number | null;
  /** When the learner finished it. `endAt` is the completion stamp; `createdAt` is the fallback. */
  completedAt: number | null;
  /** Questions the learner actually answered (skipped/unscored ones excluded). */
  answeredCount: number;
  /** Questions the assignment contained. */
  questionCount: number;
  /** 0–1 mean over the answered questions, or null when nothing was answered. */
  score: number | null;
  timeSpentMs: number;
}

/** All the readiness work a learner has done on ONE prerequisite konu. */
export interface PrerequisiteWorkGroup {
  categoryId: number;
  /** Resolved from the local category list; null when the id is outside the profile's curriculum. */
  categoryName: string | null;
  /** The konu's own grade — a prerequisite usually sits BELOW the learner's grade. */
  gradeLevel: number | null;
  attemptCount: number;
  answeredCount: number;
  questionCount: number;
  /** 0–1 mean over every answered question in the group, or null when nothing was answered. */
  avgScore: number | null;
  totalTimeSpentMs: number;
  lastWorkedAt: number | null;
  assignmentRows: PrerequisiteWorkAssignmentRow[];
}

export interface PrerequisiteWorkOptions {
  /** The report's ders filter. `DEFAULT_OPTION` (or absent) means "every ders". */
  coterieType?: string;
  /** The Genel durum time window; absent or `"all"` keeps everything. */
  timeFilterWithN?: string;
}

/**
 * A question counts as solved when the learner actually answered it. `empty` is the wire name of the
 * DTO's `isEmpty` (Lombok's `boolean isEmpty` serializes as `empty`) and marks a skipped question;
 * a null score is an unevaluated one. This is the same pair `ReportDetailBuilder` and the backend's
 * own `learnerScore` use, so a konu's average here agrees with every other score in the report.
 */
const isAnswered = (detail: any): boolean =>
  detail != null && !detail.empty && detail.score != null;

/**
 * The prerequisite konu a readiness assignment belongs to. `triggerId` IS that categoryId by
 * construction (the learner app posts the prerequisite's id when it creates the assignment).
 * `categoryIdList` — which arrives heaviest-weight first — is a fallback for a row written before
 * the trigger fields existed. Neither present means the row cannot be attributed, so it is skipped
 * rather than lumped into a bucket it may not belong to.
 */
const resolveCategoryId = (item: any): number | null => {
  if (item?.triggerId != null) return Number(item.triggerId);
  const firstCategoryId = Array.isArray(item?.categoryIdList)
    ? item.categoryIdList[0]
    : null;
  return firstCategoryId != null ? Number(firstCategoryId) : null;
};

/**
 * Groups a learner's completed readiness practice by prerequisite konu.
 *
 * @param personalizedAssignmentItems the UNFILTERED personalized-assignment performance list —
 *   this function applies its own trigger, coterie and time filtering.
 * @param localCategoryDTOList the category list the user context already holds. Names and grades are
 *   resolved from it locally; a category is NEVER fetched by id (see CLAUDE.md).
 * @param options the report's ders + time-window filters.
 */
export const buildPrerequisiteWorkGroups = (
  personalizedAssignmentItems: any[],
  localCategoryDTOList: any[],
  options: PrerequisiteWorkOptions = {},
): PrerequisiteWorkGroup[] => {
  const { coterieType, timeFilterWithN } = options;

  const categoryById = new Map<number, any>();
  (localCategoryDTOList || []).forEach((category: any) => {
    if (category?.id != null) {
      categoryById.set(Number(category.id), category);
    }
  });

  const groupByCategoryId = new Map<number, PrerequisiteWorkGroup>();

  (personalizedAssignmentItems || []).forEach((item: any) => {
    if (item?.triggerType !== "READINESS") return;
    // The solved-only gate. Evaluations — and therefore isEvaluated — exist only after the learner
    // completed the assignment, so an in-progress or abandoned one never reaches the report.
    if (item?.isEvaluated !== true) return;
    // A ders filter mirrors every other list in this report: an item whose coterie could not be
    // resolved drops out of a specific ders rather than being shown under one it may not belong to.
    if (
      coterieType &&
      coterieType !== "DEFAULT_OPTION" &&
      item?.coterieType !== coterieType
    ) {
      return;
    }
    // The completion stamp, which is what the drill-down dates and what the window filters on.
    const completedAt = item?.endAt ?? item?.createdAt ?? null;
    if (!isWithinTimeFilterWindow(completedAt, timeFilterWithN)) return;

    const categoryId = resolveCategoryId(item);
    if (categoryId == null) return;

    const detailList: any[] = Array.isArray(item?.activityScoreDetailDTOList)
      ? item.activityScoreDetailDTOList
      : [];
    const answeredDetailList = detailList.filter(isAnswered);
    const scoreSum = answeredDetailList.reduce(
      (total, detail) => total + Number(detail.score),
      0,
    );
    const timeSpentMs = detailList.reduce(
      (total, detail) => total + (Number(detail?.timeSpent) || 0),
      0,
    );

    let group = groupByCategoryId.get(categoryId);
    if (!group) {
      const category = categoryById.get(categoryId);
      group = {
        categoryId,
        categoryName: category?.name ?? null,
        gradeLevel: category?.level != null ? Number(category.level) : null,
        attemptCount: 0,
        answeredCount: 0,
        questionCount: 0,
        avgScore: null,
        totalTimeSpentMs: 0,
        lastWorkedAt: null,
        assignmentRows: [],
      };
      groupByCategoryId.set(categoryId, group);
    }

    group.attemptCount += 1;
    group.answeredCount += answeredDetailList.length;
    group.questionCount += detailList.length;
    group.totalTimeSpentMs += timeSpentMs;
    if (
      completedAt != null &&
      (group.lastWorkedAt == null || completedAt > group.lastWorkedAt)
    ) {
      group.lastWorkedAt = completedAt;
    }
    group.assignmentRows.push({
      personalizedAssignmentId: item?.personalizedAssignmentId ?? null,
      completedAt,
      answeredCount: answeredDetailList.length,
      questionCount: detailList.length,
      // Recomputed from the questions rather than read off `learnerScore`: the backend coerces that
      // field to 0.0 when a completion answered nothing, and a plotted 0% would report a collapse
      // the learner never had. Nothing answered is an absence of evidence, so it stays null.
      score: answeredDetailList.length > 0 ? scoreSum / answeredDetailList.length : null,
      timeSpentMs,
    });
  });

  const groupList = [...groupByCategoryId.values()];

  groupList.forEach((group) => {
    // A per-QUESTION mean, not a mean of the assignments' means: a one-question retry must not weigh
    // the same as a ten-question session. The per-assignment figures stay visible in the drill-down.
    const answeredScoreSum = group.assignmentRows.reduce(
      (total, row) => total + (row.score != null ? row.score * row.answeredCount : 0),
      0,
    );
    group.avgScore =
      group.answeredCount > 0 ? answeredScoreSum / group.answeredCount : null;
    group.assignmentRows.sort(
      (a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0),
    );
  });

  groupList.sort((a, b) => (b.lastWorkedAt ?? 0) - (a.lastWorkedAt ?? 0));

  return groupList;
};
